<template>
  <div class="plugin-orders">
    <div class="panel-page">
      <div class="panel-heading">
        <h2>我的插件订单</h2>
      </div>

      <el-row :gutter="12" class="stat-row">
        <el-col :span="6">
          <div class="stat-card">
            <p class="stat-num">{{ summary.orderCount || 0 }}</p>
            <p class="stat-label">成交订单（单）</p>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <p class="stat-num">{{ toYuan(summary.payerSum) }}</p>
            <p class="stat-label">买家实付（元）</p>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <p class="stat-num warning">{{ toYuan(summary.feeSum) }}</p>
            <p class="stat-label">平台手续费（元）</p>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <p class="stat-num danger">{{ toYuan(summary.incomeSum) }}</p>
            <p class="stat-label">我的收益（元）</p>
          </div>
        </el-col>
      </el-row>

      <p class="tip-line">
        收益按订单实付金额扣除平台手续费后计算，买家信息已脱敏展示。手续费与冻结规则以平台设置为准。
      </p>

      <el-form :inline="true" size="small" class="filter-bar">
        <el-form-item label="插件">
          <el-select v-model="queryParams.orderPluginId" placeholder="全部插件" clearable style="width: 180px">
            <el-option v-for="item in pluginOptions" :key="item.pluginId" :label="item.pluginName" :value="item.pluginId" />
          </el-select>
        </el-form-item>
        <el-form-item label="支付状态">
          <el-select v-model="queryParams.status" placeholder="全部状态" clearable style="width: 140px">
            <el-option label="支付成功" value="SUCCESS" />
            <el-option label="未支付" value="NOTPAY" />
            <el-option label="已关闭" value="CLOSED" />
            <el-option label="已退款" value="REFUND" />
          </el-select>
        </el-form-item>
        <el-form-item label="支付方式">
          <el-select v-model="queryParams.paymentType" placeholder="全部方式" clearable style="width: 140px">
            <el-option label="微信支付" value="wechatPay" />
            <el-option label="支付宝" value="aliPay" />
          </el-select>
        </el-form-item>
        <el-form-item label="下单时间">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="-"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            style="width: 240px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleQuery">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="loading" :data="list" style="width: 100%">
        <el-table-column label="商户订单号" prop="outTradeNo" width="200" />
        <el-table-column label="插件名称" prop="pluginName" min-width="160" show-overflow-tooltip />
        <el-table-column label="授权等级" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.priceLevel === 2 ? 'danger' : 'info'" size="small">
              {{ scope.row.priceLevel === 2 ? '高级版' : '基础版' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="实付金额（元）" width="120">
          <template slot-scope="scope">{{ scope.row.payerTotalStr || toYuan(scope.row.payerTotal) }}</template>
        </el-table-column>
        <el-table-column label="我的收益（元）" width="120">
          <template slot-scope="scope">
            <span class="income">{{ scope.row.orderAmountStr || '0.00' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="平台手续费（元）" width="130">
          <template slot-scope="scope">{{ scope.row.feeAmountStr || '0.00' }}</template>
        </el-table-column>
        <el-table-column label="支付方式" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.paymentType === 'wechatPay' ? 'success' : ''" size="small">
              {{ scope.row.paymentType === 'wechatPay' ? '微信' : (scope.row.paymentType === 'aliPay' ? '支付宝' : scope.row.paymentType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="支付状态" width="110">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 'SUCCESS' ? 'success' : 'warning'" size="small">
              {{ statusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="买家" width="170">
          <template slot-scope="scope">
            <p class="buyer">{{ scope.row.buyerNickname || '匿名用户' }}</p>
            <p class="buyer-phone">{{ scope.row.buyerPhone }}</p>
          </template>
        </el-table-column>
        <el-table-column label="下单时间" width="170">
          <template slot-scope="scope">{{ formatTime(scope.row.createdAt) }}</template>
        </el-table-column>
      </el-table>

      <div v-if="!loading && list.length === 0" class="empty-box">
        <i class="el-icon-shopping-cart-full empty-icon"></i>
        <p>暂时还没有订单</p>
      </div>

      <el-pagination
        v-if="total > 0"
        class="pager"
        background
        layout="total, prev, pager, next"
        :total="total"
        :current-page.sync="queryParams.pageNum"
        :page-size="queryParams.pageSize"
        @current-change="getList" />
    </div>
  </div>
</template>

<script>
import { listMyOrders, listMyPlugins } from '@/api/developer'
import { dateFormat } from '@/utils/common'

export default {
  name: 'pluginOrders',
  data () {
    return {
      loading: true,
      list: [],
      total: 0,
      summary: {},
      pluginOptions: [],
      dateRange: [],
      queryParams: {
        pageNum: 1,
        pageSize: 10,
        orderPluginId: '',
        status: '',
        paymentType: '',
        beginTime: '',
        endTime: ''
      }
    }
  },
  methods: {
    toYuan (value) {
      return (Number(value || 0) / 100).toFixed(2)
    },
    formatTime (value) {
      return value ? dateFormat(value, 'YYYY-MM-DD HH:mm') : '-'
    },
    statusText (status) {
      const map = {
        SUCCESS: '支付成功',
        NOTPAY: '未支付',
        CLOSED: '已关闭',
        REFUND: '已退款',
        USERPAYING: '支付中',
        PAYERROR: '支付失败'
      }
      return map[status] || status
    },
    loadPluginOptions () {
      listMyPlugins({ pageNum: 1, pageSize: 200 }).then(res => {
        const { code, data } = res
        if (code === 0) {
          this.pluginOptions = data.list || []
        }
      })
    },
    buildParams () {
      const params = Object.assign({}, this.queryParams)
      if (this.dateRange && this.dateRange.length === 2) {
        params.beginTime = this.dateRange[0] + ' 00:00:00'
        params.endTime = this.dateRange[1] + ' 23:59:59'
      }
      return params
    },
    getList () {
      this.loading = true
      listMyOrders(this.buildParams()).then(res => {
        const { code, data, msg } = res
        if (code === 0) {
          this.list = data.list || []
          this.total = data.total || 0
          this.summary = data.summary || {}
        } else {
          this.$message.error(msg || '获取订单失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    handleQuery () {
      this.queryParams.pageNum = 1
      this.getList()
    },
    resetQuery () {
      this.dateRange = []
      this.queryParams.orderPluginId = ''
      this.queryParams.status = ''
      this.queryParams.paymentType = ''
      this.handleQuery()
    }
  },
  created () {
    this.loadPluginOptions()
    this.getList()
  }
}
</script>

<style lang="less" scoped>
.plugin-orders {
  .panel-heading h2 {
    margin: 0 0 20px 0;
    font-size: 22px;
    color: #303133;
  }

  .stat-row {
    margin-bottom: 12px;
  }

  .stat-card {
    padding: 16px 12px;
    text-align: center;
    border: 1px solid #ebeef5;
    border-radius: 6px;

    .stat-num {
      margin: 0;
      font-size: 22px;
      font-weight: 600;
      color: #303133;
      font-family: 'SF Mono', Consolas, Menlo, monospace;

      &.warning {
        color: #e6a23c;
      }

      &.danger {
        color: #f56c6c;
      }
    }

    .stat-label {
      margin: 6px 0 0 0;
      font-size: 13px;
      color: #909399;
    }
  }

  .tip-line {
    margin: 0 0 16px 0;
    font-size: 12px;
    color: #909399;
  }

  .filter-bar {
    margin-bottom: 8px;
  }

  .income {
    color: #f56c6c;
    font-weight: 600;
  }

  .buyer {
    margin: 0;
    color: #303133;
  }

  .buyer-phone {
    margin: 2px 0 0 0;
    font-size: 12px;
    color: #909399;
  }

  .empty-box {
    padding: 50px 0;
    text-align: center;
    color: #909399;

    .empty-icon {
      font-size: 48px;
      color: #c0c4cc;
    }

    p {
      margin: 14px 0 0 0;
    }
  }

  .pager {
    margin-top: 18px;
    text-align: right;
  }
}
</style>
